import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Inbox, Calendar, MessageSquare, Bot, Loader2, Trash2 } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = { form: "Formulario", chatbot: "Chatbot", "chatbot-auto": "Chatbot (auto)" };

export default function AdminPanel() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inq, st] = await Promise.all([
        axios.get(`${API}/inquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setInquiries(inq.data);
      setStats(st.data);
    } catch {
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0D14]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const toggleStatus = async (inq) => {
    const next = inq.status === "contacted" ? "pending" : "contacted";
    setInquiries((list) => list.map((i) => (i.id === inq.id ? { ...i, status: next } : i)));
    try {
      await axios.patch(`${API}/inquiries/${inq.id}`, { status: next }, { withCredentials: true });
    } catch {
      setInquiries((list) => list.map((i) => (i.id === inq.id ? { ...i, status: inq.status } : i)));
    }
  };

  const deleteInquiry = async (inq) => {
    if (!window.confirm(`¿Eliminar la consulta de ${inq.name || inq.email}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/inquiries/${inq.id}`, { withCredentials: true });
      setInquiries((list) => list.filter((i) => i.id !== inq.id));
      setStats((s) => (s ? { ...s, total: s.total - 1 } : s));
    } catch {
      alert("No se pudo eliminar. Intenta de nuevo.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    navigate("/admin/login");
  };

  const statCards = stats
    ? [
        { icon: Inbox, label: "Total consultas", value: stats.total, testid: "admin-stat-total" },
        { icon: Calendar, label: "Hoy", value: stats.today, testid: "admin-stat-today" },
        { icon: MessageSquare, label: "Por formulario", value: stats.from_form, testid: "admin-stat-form" },
        { icon: Bot, label: "Por chatbot", value: stats.from_chat, testid: "admin-stat-chat" },
      ]
    : [];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0D14] px-5 py-8 text-slate-100 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div className="flex items-center gap-3">
            <LogoMark size={38} />
            <div>
              <h1 className="font-display text-2xl font-bold text-white">Panel de control</h1>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Sagas Logistic</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-user-badge" className="rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-xs font-semibold text-amber-300">
              {user.username}
            </span>
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors hover:border-amber-400/40 hover:text-amber-300"
              aria-label="Actualizar"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-red-400/40 hover:text-red-300"
            >
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </header>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {statCards.map((s) => (
            <motion.div
              key={s.testid}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-white/10 bg-[#121722] p-5"
            >
              <s.icon className="h-5 w-5 text-amber-400" />
              <div className="font-display mt-3 text-3xl font-bold text-white">{s.value}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10">
          <h2 className="font-display text-xl font-semibold text-white">
            Consultas recibidas {stats && <span className="text-sm font-normal text-amber-400">({stats.pending} pendientes)</span>}
          </h2>

          {loading ? (
            <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>
          ) : inquiries.length === 0 ? (
            <p data-testid="admin-empty-state" className="mt-6 rounded-xl border border-white/10 bg-[#121722] p-8 text-center text-sm text-slate-400">
              Aún no hay consultas. Las del formulario y el chatbot aparecerán aquí.
            </p>
          ) : (
            <div className="mt-5 space-y-3">
              {inquiries.map((inq) => (
                <div
                  key={inq.id}
                  data-testid={`inquiry-row-${inq.id}`}
                  className={`rounded-xl border p-5 transition-colors ${
                    inq.status === "contacted" ? "border-white/5 bg-[#0C1018] opacity-70" : "border-white/10 bg-[#121722]"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-white">{inq.name || "Sin nombre"}</span>
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          inq.source === "form" ? "bg-amber-400/15 text-amber-300" : "bg-emerald-400/15 text-emerald-300"
                        }`}>
                          {SOURCE_LABELS[inq.source] || inq.source}
                        </span>
                        <span className="rounded-full bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          {inq.lang}
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                        {inq.email && <span>{inq.email}</span>}
                        {inq.phone && <span>{inq.phone}</span>}
                        {inq.service && <span className="text-slate-300">{inq.service}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-[11px] text-slate-500">{new Date(inq.created_at).toLocaleString("es-VE")}</span>
                      <button
                        data-testid={`inquiry-status-toggle-${inq.id}`}
                        onClick={() => toggleStatus(inq)}
                        className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                          inq.status === "contacted"
                            ? "bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25"
                            : "bg-amber-400/15 text-amber-300 hover:bg-amber-400/25"
                        }`}
                      >
                        {inq.status === "contacted" ? "Contactado" : "Pendiente"}
                      </button>
                      <button
                        data-testid={`inquiry-delete-${inq.id}`}
                        onClick={() => deleteInquiry(inq)}
                        aria-label="Eliminar consulta"
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-slate-500 transition-colors hover:border-red-400/50 hover:text-red-300"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  {inq.message && <p className="mt-3 border-t border-white/5 pt-3 text-sm leading-relaxed text-slate-300">{inq.message}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
